/**
 * Tenant Context - Multi-Tenant Context Provider
 *
 * Provides React context for current tenant/account information.
 * Extracts tenant from subdomain and provides hooks for tenant-aware operations.
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import type { Account, AccountId } from '../domain/account';
import { getAccountByDomain, getAccountBySlug } from '../services/accountService';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Tenant context value
 */
export interface TenantContextValue {
  // Current account/tenant
  account: Account | null;
  accountId: AccountId | null;

  // Loading state
  isLoading: boolean;
  error: string | null;

  // Tenant info
  slug: string | null;
  domain: string | null;
  schemaName: string | null;

  // Is this a valid tenant subdomain?
  isTenantSubdomain: boolean;

  // Is this the main app (no tenant)?
  isMainApp: boolean;

  // Actions
  refreshAccount: () => Promise<void>;
  setAccount: (account: Account | null) => void;
}

// Default context value
const defaultContext: TenantContextValue = {
  account: null,
  accountId: null,
  isLoading: true,
  error: null,
  slug: null,
  domain: null,
  schemaName: null,
  isTenantSubdomain: false,
  isMainApp: true,
  refreshAccount: async () => {},
  setAccount: () => {},
};

// =============================================================================
// CONTEXT
// =============================================================================

const TenantContext = createContext<TenantContextValue>(defaultContext);

// =============================================================================
// SUBDOMAIN EXTRACTION
// =============================================================================

/**
 * Base domain for the platform
 */
const BASE_DOMAIN = 'ba.teknology.eu';

/**
 * List of reserved subdomains that are not tenant slugs
 */
const RESERVED_SUBDOMAINS = new Set([
  'www',
  'api',
  'admin',
  'app',
  'dashboard',
  'store',
  'storage',
  'files',
  'cdn',
  'assets',
  'static',
  'graphql',
  'ws',
  'auth',
  'mail',
  'smtp',
  'imap',
]);

/**
 * Extract tenant info from hostname
 */
export interface TenantFromHost {
  slug: string | null;
  domain: string;
  isTenantSubdomain: boolean;
  isMainApp: boolean;
  isCustomDomain: boolean;
}

export function getTenantFromHost(hostname: string): TenantFromHost {
  const domain = hostname.toLowerCase();

  // Check if it's the main app (no subdomain)
  if (domain === BASE_DOMAIN || domain === `www.${BASE_DOMAIN}`) {
    return {
      slug: null,
      domain,
      isTenantSubdomain: false,
      isMainApp: true,
      isCustomDomain: false,
    };
  }

  // Check if it's a subdomain of base domain
  if (domain.endsWith(`.${BASE_DOMAIN}`)) {
    const subdomain = domain.replace(`.${BASE_DOMAIN}`, '');

    // Check if it's a reserved subdomain
    if (RESERVED_SUBDOMAINS.has(subdomain)) {
      return {
        slug: null,
        domain,
        isTenantSubdomain: false,
        isMainApp: subdomain === 'www' || subdomain === 'app',
        isCustomDomain: false,
      };
    }

    // It's a tenant subdomain
    return {
      slug: subdomain,
      domain,
      isTenantSubdomain: true,
      isMainApp: false,
      isCustomDomain: false,
    };
  }

  // It's a custom domain
  return {
    slug: null,
    domain,
    isTenantSubdomain: false,
    isMainApp: false,
    isCustomDomain: true,
  };
}

/**
 * Extract tenant slug from current window location
 */
export function getTenantFromSubdomain(): TenantFromHost {
  if (typeof window === 'undefined') {
    return {
      slug: null,
      domain: '',
      isTenantSubdomain: false,
      isMainApp: true,
      isCustomDomain: false,
    };
  }

  return getTenantFromHost(window.location.hostname);
}

// =============================================================================
// PROVIDER
// =============================================================================

export interface TenantProviderProps {
  children: ReactNode;
  // Optional: manually provide account (for testing or pre-loaded data)
  initialAccount?: Account | null;
  // Optional: skip auto-detection (for main app)
  skipAutoDetect?: boolean;
}

export function TenantProvider({
  children,
  initialAccount = null,
  skipAutoDetect = false,
}: TenantProviderProps) {
  const [account, setAccount] = useState<Account | null>(initialAccount);
  const [isLoading, setIsLoading] = useState(!initialAccount && !skipAutoDetect);
  const [error, setError] = useState<string | null>(null);

  // Get tenant info from hostname
  const tenantInfo = getTenantFromSubdomain();

  // Load account from subdomain/domain
  const loadAccount = useCallback(async () => {
    if (skipAutoDetect || tenantInfo.isMainApp) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let loadedAccount: Account | null = null;

      if (tenantInfo.isTenantSubdomain && tenantInfo.slug) {
        // Load by slug
        loadedAccount = await getAccountBySlug(tenantInfo.slug);
      } else if (tenantInfo.isCustomDomain) {
        // Load by custom domain
        loadedAccount = await getAccountByDomain(tenantInfo.domain);
      }

      if (loadedAccount) {
        setAccount(loadedAccount);
      } else if (tenantInfo.isTenantSubdomain || tenantInfo.isCustomDomain) {
        setError('Account not found');
      }
    } catch (err) {
      console.error('[TenantContext] Failed to load account:', err);
      setError(err instanceof Error ? err.message : 'Failed to load account');
    } finally {
      setIsLoading(false);
    }
  }, [tenantInfo.slug, tenantInfo.domain, tenantInfo.isTenantSubdomain, tenantInfo.isCustomDomain, tenantInfo.isMainApp, skipAutoDetect]);

  // Load account on mount
  useEffect(() => {
    if (!initialAccount && !skipAutoDetect) {
      loadAccount();
    }
  }, [loadAccount, initialAccount, skipAutoDetect]);

  // Refresh account
  const refreshAccount = useCallback(async () => {
    await loadAccount();
  }, [loadAccount]);

  // Context value
  const value: TenantContextValue = {
    account,
    accountId: account?.id ?? null,
    isLoading,
    error,
    slug: tenantInfo.slug,
    domain: tenantInfo.domain,
    schemaName: account?.schemaName ?? null,
    isTenantSubdomain: tenantInfo.isTenantSubdomain,
    isMainApp: tenantInfo.isMainApp,
    refreshAccount,
    setAccount,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Hook to access tenant context
 */
export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

/**
 * Hook to get current account ID (throws if not in tenant context)
 */
export function useAccountId(): AccountId {
  const { accountId } = useTenant();
  if (!accountId) {
    throw new Error('useAccountId: No account in context');
  }
  return accountId;
}

/**
 * Hook to check if we're in a tenant context
 */
export function useIsTenant(): boolean {
  const { account, isTenantSubdomain } = useTenant();
  return !!account || isTenantSubdomain;
}

/**
 * Hook to get tenant storage path prefix
 */
export function useTenantStoragePath(): string {
  const { accountId } = useTenant();
  if (!accountId) {
    return 'public';
  }
  return `accounts/${accountId}`;
}

// =============================================================================
// UTILITIES
// =============================================================================

/**
 * Build full subdomain URL
 */
export function buildSubdomainUrl(slug: string): string {
  return `https://${slug}.${BASE_DOMAIN}`;
}

/**
 * Get current tenant subdomain URL
 */
export function getCurrentTenantUrl(): string | null {
  const tenantInfo = getTenantFromSubdomain();
  if (tenantInfo.slug) {
    return buildSubdomainUrl(tenantInfo.slug);
  }
  return null;
}

/**
 * Check if a URL belongs to a tenant
 */
export function isTenantUrl(url: string): boolean {
  try {
    const { hostname } = new URL(url);
    const tenantInfo = getTenantFromHost(hostname);
    return tenantInfo.isTenantSubdomain || tenantInfo.isCustomDomain;
  } catch {
    return false;
  }
}

/**
 * Extract account slug from URL
 */
export function extractSlugFromUrl(url: string): string | null {
  try {
    const { hostname } = new URL(url);
    const tenantInfo = getTenantFromHost(hostname);
    return tenantInfo.slug;
  } catch {
    return null;
  }
}

// =============================================================================
// HTTP HEADERS
// =============================================================================

/**
 * Get tenant headers for API requests
 */
export function getTenantHeaders(account: Account | null): Record<string, string> {
  if (!account) {
    return {};
  }

  return {
    'X-Tenant-ID': account.id,
    'X-Tenant-Slug': account.slug,
    'X-Tenant-Schema': account.schemaName,
  };
}

/**
 * Create fetch wrapper with tenant headers
 */
export function createTenantFetch(account: Account | null): typeof fetch {
  return (input: RequestInfo | URL, init?: RequestInit) => {
    const headers = new Headers(init?.headers);
    const tenantHeaders = getTenantHeaders(account);

    Object.entries(tenantHeaders).forEach(([key, value]) => {
      headers.set(key, value);
    });

    return fetch(input, { ...init, headers });
  };
}

// =============================================================================
// EXPORTS
// =============================================================================

export { TenantContext };
export type { TenantFromHost };
